import { startTransition, useEffect, useState } from "react";
import { type Promotion, salePriceCentsFor } from "@glastotaal/core";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";
import { firebaseIdle } from "@/lib/firebase/app";
import { bewaarStartwaarde, leesStartwaarde, zelfdeWaarde } from "@/lib/start-cache";

/**
 * Runtime acties/uitlichten: gelezen uit Firestore `promotions` (admin-beheerd,
 * publiek leesbaar). Eén gecachte read per sessie; de server (createOrder) rekent
 * de actieprijs onafhankelijk + autoritatief door. Klein collectie → flat ophalen.
 */
export type PromoMap = Record<string, Promotion>;

// Begint met wat deze browser de vorige keer las (zie start-cache.ts): anders stond bij elk
// bezoek eerst de prijs zónder actie in beeld en verscheen de korting pas een seconde later.
let cached: PromoMap | null = leesStartwaarde<PromoMap>("promotions");
/** Is `cached` al de live waarde van dít bezoek? */
let live = false;
// Meerdere secties (SiteLayout, SearchBar, FeaturedDeals, RecentlyViewed, ...) monteren deze hook
// tegelijk op de homepage. Zonder deze in-flight-gate zag elke instantie `cached === null` en
// startte elk zíjn EIGEN `readPromos()` — tot 6 identieke Firestore-requests i.p.v. 1.
let pending: Promise<PromoMap> | null = null;

async function readPromos(): Promise<PromoMap | null> {
  try {
    const [{ collection, getDocs }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const snap = await getDocs(collection(db, "promotions"));
    const map: PromoMap = {};
    for (const d of snap.docs) {
      const p = d.data() as Promotion;
      const slug = p.slug ?? d.id;
      map[slug] = { ...p, slug };
    }
    return map;
  } catch {
    // null, niet {}: een mislukte read mag de bewaarde acties niet wissen.
    return null;
  }
}

function loadOnce(): Promise<PromoMap> {
  if (live && cached != null) return Promise.resolve(cached);
  if (!pending) {
    pending = firebaseIdle().then(readPromos).then((m) => {
      pending = null;
      live = true;
      if (m == null) return cached ?? {};
      bewaarStartwaarde("promotions", m);
      // Zelfde acties als waarmee we begonnen? Dan hetzelfde object teruggeven, zodat React
      // niets opnieuw tekent.
      if (!zelfdeWaarde(m, cached)) cached = m;
      return cached ?? m;
    });
  }
  return pending;
}

/**
 * Of de acties al binnen zijn. "Nu in de aanbieding" op de homepage rendert `null` tot er
 * data is — zonder dit signaal verschijnt dat hele blok (en alles eronder schuift mee) pas
 * ná de idle-Firestore-read, wat Lighthouse als Cumulative Layout Shift afstraft. Met dit
 * signaal kan zo'n sectie een gelijkvormig skelet reserveren tot het antwoord er is.
 */
export function usePromotionsReady(): boolean {
  const [ready, setReady] = useState(cached != null);
  useEffect(() => {
    if (cached != null) { setReady(true); return; }
    void loadOnce().then(() => startTransition(() => setReady(true)));
  }, []);
  return ready;
}

/** Live acties; lege map tot de Firestore-read binnen is. */
export function usePromotions(): PromoMap {
  const [v, setV] = useState<PromoMap>(cached ?? {});
  useEffect(() => {
    if (live && cached != null) return;
    // Meerdere secties wachten tegelijk op dezelfde `pending`-belofte (SiteLayout, SearchBar×2,
    // FeaturedDeals, RecentlyViewed, ...) en resolven dus allemaal in dezelfde tick — zonder
    // startTransition zet React al hun re-renders in ÉÉN synchrone, niet-onderbreekbare taak.
    void loadOnce().then((m) => startTransition(() => setV(m)));
  }, []);
  return v;
}

/** One-off fetch (forms/admin); ververst de cache. */
export async function fetchPromotions(): Promise<PromoMap> {
  const m = (await readPromos()) ?? {};
  cached = m;
  live = true;
  bewaarStartwaarde("promotions", m);
  return m;
}

export function setCachedPromotion(p: Promotion): void {
  cached = { ...(cached ?? {}), [p.slug]: p };
  bewaarStartwaarde("promotions", cached);
}

export function removeCachedPromotion(slug: string): void {
  if (!cached) return;
  const next = { ...cached };
  delete next[slug];
  cached = next;
  bewaarStartwaarde("promotions", cached);
}

/**
 * Override-veld voor de homepage: `"-"` = expliciet leeg (verberg het vak),
 * leeg = de automatische standaard, anders de ingevulde waarde.
 */
export function overrideField(value: string | undefined, fallback: string): string {
  const v = (value ?? "").trim();
  if (v === "-") return "";
  return v || fallback;
}

export type Placement = "row" | "hero" | "deals";

/** De homepage-plekken waar een promotie verschijnt. Leest de nieuwe `placements`-array
 *  en valt terug op het oude `featured` + `placement` (backward-compat). */
export function placementSet(p: Promotion): Set<Placement> {
  if (Array.isArray(p.placements)) return new Set(p.placements);
  if (!p.featured) return new Set();
  const pl = p.placement ?? "row";
  if (pl === "both") return new Set(["row", "hero"]);
  return new Set(pl === "hero" || pl === "deals" ? [pl] : ["row"]);
}

/** True = de prijs wordt bewust verborgen (toon overal "Prijs op aanvraag"). */
export function isPriceHidden(promo: Promotion | undefined): boolean {
  return !!promo && promo.active !== false && promo.hidePrice === true;
}

/** Effectieve prijs + 'van'-prijs (excl. btw, centen) voor een artikel. */
export function priceWithSale(baseCents: number, promo: Promotion | undefined): { priceCents: number; wasCents: number | null } {
  const sale = salePriceCentsFor(baseCents, promo);
  return sale == null ? { priceCents: baseCents, wasCents: null } : { priceCents: sale, wasCents: baseCents };
}
